import type { MediaCapabilityProfile, RecorderProfile } from './types.ts';

const DEFAULT_VIDEO_BITS = 4_500_000;
const DEFAULT_AUDIO_BITS = 128_000;

function applePlatform(userAgent: string): boolean {
  return /iPad|iPhone|iPod|Macintosh/.test(userAgent) && /Safari|AppleWebKit/.test(userAgent);
}

function candidates(userAgent: string): readonly { mimeType: string; extension: 'mp4' | 'webm' }[] {
  const mp4 = [
    { mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', extension: 'mp4' as const },
    { mimeType: 'video/mp4;codecs=h264,aac', extension: 'mp4' as const },
    { mimeType: 'video/mp4', extension: 'mp4' as const },
  ];
  const webm = [
    { mimeType: 'video/webm;codecs=vp8,opus', extension: 'webm' as const },
    { mimeType: 'video/webm;codecs=vp9,opus', extension: 'webm' as const },
    { mimeType: 'video/webm', extension: 'webm' as const },
  ];
  return applePlatform(userAgent) ? [...mp4, ...webm] : [...webm, ...mp4];
}

function profile(
  mimeType: string,
  extension: 'mp4' | 'webm',
  quality: 'default' | 'fallback',
): RecorderProfile {
  return {
    mimeType,
    extension,
    width: quality === 'default' ? 720 : 540,
    height: quality === 'default' ? 1280 : 960,
    fps: quality === 'default' ? 30 : 24,
    videoBitsPerSecond: quality === 'default' ? DEFAULT_VIDEO_BITS : 2_500_000,
    audioBitsPerSecond: DEFAULT_AUDIO_BITS,
  };
}

export function detectMediaCapabilities(
  environment: {
    userAgent?: string;
    mediaRecorder?: typeof MediaRecorder;
    navigator?: Navigator;
    canvasCaptureSupported?: boolean;
  } = {},
): MediaCapabilityProfile {
  const Recorder = environment.mediaRecorder ?? globalThis.MediaRecorder;
  const navigatorValue = environment.navigator ?? globalThis.navigator;
  const canvasCaptureSupported =
    environment.canvasCaptureSupported ??
    (typeof HTMLCanvasElement !== 'undefined' &&
      Boolean(HTMLCanvasElement.prototype.captureStream));
  if (!Recorder || !canvasCaptureSupported)
    return {
      nativeFileShare: false,
      preferredRecorder: null,
      fallbackRecorder: null,
      staticOnlyReason: 'Video export is not supported in this browser. Your photo is still ready.',
    };
  const supported = candidates(environment.userAgent ?? navigatorValue?.userAgent ?? '').filter(
    (candidate) => Recorder.isTypeSupported(candidate.mimeType),
  );
  const preferred = supported[0];
  const fallback = supported[1] ?? preferred;
  return {
    nativeFileShare: Boolean(
      navigatorValue && 'share' in navigatorValue && 'canShare' in navigatorValue,
    ),
    preferredRecorder: preferred
      ? profile(preferred.mimeType, preferred.extension, 'default')
      : null,
    fallbackRecorder: fallback ? profile(fallback.mimeType, fallback.extension, 'fallback') : null,
    ...(!preferred
      ? {
          staticOnlyReason:
            'This browser cannot encode a compatible video. Your photo is still ready.',
        }
      : {}),
  };
}

export function canNativeShareFile(file: File, navigatorValue: Navigator = navigator): boolean {
  if (!('share' in navigatorValue) || !('canShare' in navigatorValue)) return false;
  try {
    return navigatorValue.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export function createMediaShareData(
  file: File,
  coasterTitle: string,
  publicUrl: string | null,
): ShareData {
  return {
    files: [file],
    title: coasterTitle,
    text: `Ride ${coasterTitle} on Buncha Coasters.${publicUrl ? ` ${publicUrl}` : ''}`,
  };
}

export function classifyMediaShareError(cause: unknown): {
  readonly outcome: 'cancelled' | 'failed';
  readonly failureCode: 'AbortError' | 'DataError' | 'NotAllowedError' | 'TypeError' | 'unknown';
  readonly message: string;
} {
  const errorName = cause instanceof Error ? cause.name : 'UnknownError';
  if (errorName === 'AbortError')
    return {
      outcome: 'cancelled',
      failureCode: 'AbortError',
      message: 'Share was closed or no share target was available. Your file is still ready.',
    };
  if (errorName === 'DataError')
    return {
      outcome: 'failed',
      failureCode: 'DataError',
      message: 'This device could not send the file to a share target. Use Download instead.',
    };
  if (errorName === 'NotAllowedError' || errorName === 'TypeError')
    return {
      outcome: 'failed',
      failureCode: errorName,
      message: 'This browser cannot share this file directly. Use Download instead.',
    };
  return {
    outcome: 'failed',
    failureCode: 'unknown',
    message: 'Sharing did not complete. Your file is still ready to download.',
  };
}
